/**
 * Kanban-card representation of an Account. Renders inside AccountKanban
 * columns. Clicking the card navigates to the detail page; the drag handle
 * (the outer div) is enabled only when `_canTransition.allowedPhases` is
 * non-empty (the user has permission to move it).
 */

import React from 'react';
import {useNavigate} from 'react-router-dom';
import {AlertCircle, ArrowRight} from 'lucide-react';
import {Card} from '@common-web/components/ui/card';
import {AssignedBrokerLink} from '@common-web/components/account/AssignedBrokerLink';
import {OpenEnrollmentBadge} from '@common-web/components/account/OpenEnrollmentBadge';
import {AnalyzerGroupBadge} from '@common-web/components/account/AnalyzerGroupBadge';
import {Badge} from '@common-web/components/ui/badge';
import type {OpenEnrollmentMetadata} from '@common/lib/openEnrollment';
import type {GroupProcessType} from '@common/lib/groupProcessType';
import {initialsFromFullName} from '@common-web/lib/nameUtils';
import {formatCurrency} from '@common/lib/utils';
import type {AccountsArchiveReason, LifecyclePhase, AccountsSubStage} from '@api/generated/client';
import {
  ARCHIVE_REASON_LABEL,
  SUB_STAGE_LABEL,
  subStageAdvanceRequirement,
  isOverdue,
  toActiveSubStage,
} from '@common-web/components/account/phaseLabels';
import {useAccountsViewConfig} from '@common-web/hooks/useAccountsViewConfig';
import {useIsInternal} from '@common-web/providers/portalProvider';
import {findPositionBySystemSubStage, resolveAccountPosition} from '@common/accountsView';
import {
  CBS_PHASE_PILL_COLORS,
  CBS_STAGE_PILL_CLASS,
  cbsStagePillStyle,
} from '@common-web/components/account/cbsAccountsPresentation';

export interface AccountCardData {
  id: string;
  companyName: string;
  lifecyclePhase: LifecyclePhase;
  subStage: AccountsSubStage;
  // Custom-config position (optional; falls back to canonical resolution).
  viewStageKey?: string | null;
  viewSubStageKey?: string | null;
  dealValue: string | null;
  nextActionText: string | null;
  nextActionDueAt: Date | string | null;
  lastActivityAt: Date | string | null;
  state: string | null;
  estimatedFtEmployees: number | null;
  isArchived: boolean;
  archiveReason: AccountsArchiveReason | null;
  client: {id: string; name: string} | null;
  assignedBroker: {id: string; name: string} | null;
  group: {
    id: string;
    estimatedMemberCount: number | null;
    vbaGroupId: string | null;
    // Analyzer vs Monthly (from GroupMetadata.internalProcess) — drives the
    // card's tag. Null when the group's internalProcess is unclassified.
    groupProcessType?: GroupProcessType | null;
    // Absent on the virtual VBA cards, which have no Infinity group metadata.
    metadata?: OpenEnrollmentMetadata | null;
  } | null;
  // Legacy VBA active groups are projected into the list as read-only "virtual"
  // cards. Their `vba:<Group_ID>` id renders the same account detail page from
  // a synthesized read-only payload.
  vbaGroupId: string | null;
  isVbaGroup: boolean;
  // Per-tenant Welcome Call flag (server-resolved on account.list) — drives the
  // RFC_RECEIVED next-action fallback. Absent on virtual VBA cards.
  welcomeCallEnabled?: boolean;
  _canTransition: {
    allowedPhases: LifecyclePhase[];
    blocked: Array<{phase: LifecyclePhase; reasonCode: string; tooltip: string}>;
  };
}

interface AccountCardProps {
  account: AccountCardData;
  routePrefix: string;
  onDragStart?: () => void;
  onDragEnd?: () => void;
}

function memberCount(account: AccountCardData): number | null {
  return account.group?.estimatedMemberCount ?? account.estimatedFtEmployees;
}

// The card hides the deal-value row entirely when there is no value, so this
// returns null (not a placeholder) for missing/invalid input.
function formatDealValue(decimal: string | null): string | null {
  if (!decimal || !Number.isFinite(Number(decimal))) return null;
  return formatCurrency(decimal, {maximumFractionDigits: 0});
}

export function AccountCard({
  account,
  routePrefix,
  onDragStart,
  onDragEnd,
}: AccountCardProps): React.ReactElement {
  const navigate = useNavigate();
  const isInternal = useIsInternal();
  // Badge shows the milestone being worked toward — same node the detail
  // page's lifecycle band highlights — so list and detail always agree. The
  // milestone is labeled through the active Accounts-view config.
  const config = useAccountsViewConfig();
  const ownPosition = resolveAccountPosition(config, account);
  const activeSubStage = toActiveSubStage(account.subStage);
  const activePosition =
    activeSubStage === account.subStage
      ? ownPosition
      : findPositionBySystemSubStage(config, activeSubStage);
  // Don't advance the badge onto a milestone this tenant's pipeline omits (e.g.
  // a submitted RFC advancing to "Broker Approval" for a client-only-approval
  // tenant) — fall back to the account's own represented sub-stage so the label
  // matches the config (e.g. "RFC Submitted").
  const displaySubStage = activePosition ? activeSubStage : account.subStage;
  const displayPosition = activePosition ?? ownPosition;
  const subStageLabel = displayPosition?.subStage.label ?? SUB_STAGE_LABEL[displaySubStage];

  // Mini sub-stage tracker — how far the account is through its current stage's
  // configured sub-stages (the current milestone counts as completed, matching
  // the synrgyinfinity card). Falls back to a plain sub-stage label when the
  // stage has a single sub-stage (e.g. Active, or the granular internal config)
  // or when the account's phase isn't represented in the active config.
  const subStageSteps = displayPosition?.stage.subStages ?? [];
  const currentSubStageIdx = displayPosition
    ? subStageSteps.findIndex((s) => s.key === displayPosition.subStage.key)
    : -1;
  const completedSubStages = currentSubStageIdx >= 0 ? currentSubStageIdx + 1 : 0;
  const totalSubStages = subStageSteps.length;
  const showProgress = totalSubStages > 1 && currentSubStageIdx >= 0;

  const draggable = account._canTransition.allowedPhases.length > 0 && !account.isArchived;
  const overdue = isOverdue(account.nextActionDueAt);
  // Prefer an explicit next action; otherwise derive the sub-stage's advance
  // requirement so the card always reads like the synrgyinfinity "next action"
  // line (mirrors the Accounts table fallback).
  const nextActionText =
    account.nextActionText ??
    subStageAdvanceRequirement(account.subStage, config, {
      welcomeCallEnabled: account.welcomeCallEnabled,
    });
  const brokerInitials = account.assignedBroker
    ? initialsFromFullName(account.assignedBroker.name)
    : null;
  const dealValue = formatDealValue(account.dealValue);
  const members = memberCount(account);
  const to = `${routePrefix}/${account.id}`;

  // The card navigates on click rather than wrapping its contents in a <Link>
  // so the assigned-broker hyperlink can be a real nested anchor (nested <a>
  // tags are invalid). Mirrors the AccountsListTable row click-to-navigate.
  return (
    <Card
      className={`p-3 hover:shadow-md transition-shadow ${
        draggable ? 'cursor-grab active:cursor-grabbing' : 'cursor-pointer'
      }`}
      data-accounts-id={account.id}
      draggable={draggable}
      role='button'
      tabIndex={0}
      onClick={() => navigate(to)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          navigate(to);
        }
      }}
      onDragStart={draggable ? onDragStart : undefined}
      onDragEnd={draggable ? onDragEnd : undefined}
    >
      <div className='flex items-start justify-between gap-2'>
        <div className='font-medium text-sm truncate flex-1'>{account.companyName}</div>
        {dealValue ? (
          <div className='text-xs tabular-nums text-muted-foreground shrink-0'>{dealValue}</div>
        ) : null}
      </div>

      <div className='text-xs text-muted-foreground mt-1'>
        {members !== null ? `${members} members` : null}
        {members !== null && account.state ? ' · ' : null}
        {account.state}
      </div>

      <div className='flex flex-wrap items-center gap-1.5'>
        <OpenEnrollmentBadge metadata={account.group?.metadata} compact className='mt-1.5' />
        <AnalyzerGroupBadge
          groupProcessType={account.group?.groupProcessType}
          compact
          className='mt-1.5'
        />
      </div>

      {showProgress ? (
        <div className='mt-2 flex items-center gap-1.5'>
          <div className='flex h-[3px] flex-1 gap-[2px]'>
            {subStageSteps.map((step, i) => (
              <div
                key={step.key}
                className={`flex-1 rounded-full ${
                  i < completedSubStages ? 'bg-primary' : 'bg-muted'
                }`}
              />
            ))}
          </div>
          <span className='shrink-0 whitespace-nowrap text-xs text-muted-foreground'>
            {subStageLabel} · {completedSubStages}/{totalSubStages}
          </span>
        </div>
      ) : isInternal ? (
        // Internal cards use cbs-portal's phase-tinted StageTag pill so the
        // kanban agrees with the table and status board.
        <div className='mt-2'>
          <span
            className={CBS_STAGE_PILL_CLASS}
            style={cbsStagePillStyle(
              CBS_PHASE_PILL_COLORS[displayPosition?.stage.systemPhase ?? account.lifecyclePhase],
            )}
          >
            {subStageLabel}
          </span>
        </div>
      ) : (
        <div className='mt-2 text-xs text-muted-foreground'>{subStageLabel}</div>
      )}

      {nextActionText ? (
        <div
          className={`mt-2 flex items-start gap-1.5 text-xs ${
            overdue ? 'font-medium text-red-600' : 'text-muted-foreground'
          }`}
        >
          {overdue ? (
            <AlertCircle className='mt-[2px] h-3 w-3 shrink-0' />
          ) : (
            <ArrowRight className='mt-[2px] h-3 w-3 shrink-0' />
          )}
          <span className='leading-snug'>{nextActionText}</span>
        </div>
      ) : null}

      <div className='mt-2 flex items-center gap-1.5 border-t pt-2'>
        {brokerInitials ? (
          <span className='inline-flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-full bg-primary text-xs font-bold text-primary-foreground'>
            {brokerInitials}
          </span>
        ) : null}
        <AssignedBrokerLink broker={account.assignedBroker} stopPropagation className='text-xs' />
      </div>

      {account.isArchived ? (
        <div className='mt-2'>
          <Badge
            variant={account.archiveReason === 'ON_HOLD' ? 'secondary' : 'destructive'}
            className='text-xs'
          >
            {account.archiveReason ? ARCHIVE_REASON_LABEL[account.archiveReason] : 'Archived'}
          </Badge>
        </div>
      ) : null}
    </Card>
  );
}
